var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { MovementConstraintFacet, ConstraintType, ConstraintsAxis, worldToLocalPosition, } from "flavr-common";
import { Euler, Matrix4, Quaternion, Vector3, Vector4 } from "flavr-math";
import { Vector3 as ThreeVector3 } from "three";
import { modelPool } from "../model/ModelPool";
import { FacetHandler, FacetRemoveHandler } from "../module/messageHandlerDecorators";
import { NewModule } from "../module/NewModule";
import { navMeshService } from "../navMesh/NavMeshService";
/**
 * Applies designer-defined movement/rotation constraints (axis-locked rotation, axis-locked movement, min/max range, grid-snapping or nav-mesh clamping) to a model's world transform, keeping it constrained no matter how its transform changes, and propagates the same constraints down to descendants whenever a constrained ancestor moves.
 *
 * Because it must react to every transform change rather than just messages, it hooks directly into `modelPool`'s model-transform-changed, model-destroyed and model-parent-changed callbacks (instead of the message bus) and maintains a reverse ancestor-to-descendant index so moving an ancestor re-applies constraints to all constrained models beneath it.
 *
 * Facets:
 * - `MovementConstraintFacet` - Defines the constraints to enforce for a model, captured relative to its world transform at the time the facet is added or re-enabled; disabling the facet or removing it stops the model from being constrained.
 */
export class MovementConstraintModule extends NewModule {
    constructor() {
        super("MovementConstraintModule");
        this.constraintStates = new Map();
        // Maps each ancestor UID → set of constrained model UIDs whose parent chain passes through it
        this.ancestorToConstrained = new Map();
        // Scratch objects reused each frame to avoid GC pressure
        this.tmpVec3a = new Vector3();
        this.tmpVec3b = new Vector3();
        this.tmpVec4 = new Vector4();
        this.tmpQuat = new Quaternion();
        this.tmpEuler = new Euler();
        this.tmpWorldPos = new Vector3();
        this.tmpWorldOri = new Quaternion();
        this.tmpOrigPos = new Vector3();
        this.tmpOrigOri = new Quaternion();
        this.tmpParentMat = new Matrix4();
        // Three.js scratch vectors for navmesh queries (navMeshService uses three's Vector3)
        this.tmpNavMeshEnd = new ThreeVector3();
        this.tmpNavMeshOut = new ThreeVector3();
    }
    /// Life-cycle methods ///
    onInit() {
        this.constraintMeta = {
            source: { workerId: "", moduleId: this.moduleID },
            timeStamp: 0,
            transport: "normal",
        };
        modelPool.setOnModelTransformChangedHandler((model, _transform, meta) => {
            this.handleTransformChanged(model, meta);
        });
        modelPool.setOnModelDestroyedHandler((model) => {
            const uid = model.getUID();
            this.unregisterAncestors(uid);
            this.constraintStates.delete(uid);
        });
        modelPool.setOnModelParentChangedHandler((model) => {
            if (!this.constraintStates.has(model.getUID()))
                return;
            this.unregisterAncestors(model.getUID());
            this.registerAncestors(model.getUID(), model);
        });
    }
    /// Message handlers ///
    handleMovementConstraintFacet(uid, facet) {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        if (!facet.enabled) {
            this.unregisterAncestors(uid);
            this.constraintStates.delete(uid);
            return;
        }
        const { position, orientation } = model.getWorldTransformComponents();
        this.unregisterAncestors(uid);
        this.constraintStates.set(uid, {
            constraints: facet.constraints.map(c => this.toWireFormat(c)),
            initialWorldPosition: position.clone(),
            initialWorldRotation: orientation.clone(),
        });
        this.registerAncestors(uid, model);
    }
    handleMovementConstraintFacetRemoved(uid) {
        const model = modelPool.getModel(uid);
        if (model !== undefined)
            this.unregisterAncestors(uid);
        this.constraintStates.delete(uid);
    }
    /// Private handler methods ///
    registerAncestors(uid, model) {
        let ancestor = model.getParent();
        while (ancestor !== undefined) {
            const ancestorUID = ancestor.getUID();
            let set = this.ancestorToConstrained.get(ancestorUID);
            if (set === undefined) {
                set = new Set();
                this.ancestorToConstrained.set(ancestorUID, set);
            }
            set.add(uid);
            ancestor = ancestor.getParent();
        }
    }
    unregisterAncestors(uid) {
        for (const [ancestorUID, set] of this.ancestorToConstrained) {
            if (set.delete(uid) && set.size === 0)
                this.ancestorToConstrained.delete(ancestorUID);
        }
    }
    handleTransformChanged(model, meta) {
        // Peer transforms are authoritative; local navmeshes can produce a different Y clamp.
        if (meta.source.moduleId === this.moduleID || meta.transport === "peer")
            return;
        // Apply constraints for this model if it has a constraint state
        this.applyConstraintsToModel(model);
        // When an ancestor moves, apply constraints for all constrained descendants
        const constrained = this.ancestorToConstrained.get(model.getUID());
        if (constrained !== undefined) {
            for (const constrainedUID of constrained) {
                const constrainedModel = modelPool.getModel(constrainedUID);
                if (constrainedModel !== undefined) {
                    this.applyConstraintsToModel(constrainedModel);
                }
            }
        }
    }
    applyConstraintsToModel(model) {
        var _a;
        // Facet removal is broadcast asynchronously, but reset transforms are applied immediately.
        if (((_a = model.getFacet(MovementConstraintFacet)) === null || _a === void 0 ? void 0 : _a.enabled) !== true)
            return;
        const state = this.constraintStates.get(model.getUID());
        if (state === undefined)
            return;
        const { position, orientation, scale } = model.getWorldTransformComponents(this.tmpWorldPos, this.tmpWorldOri);
        this.tmpOrigPos.copy(position);
        this.tmpOrigOri.copy(orientation);
        for (const c of state.constraints)
            this.applyConstraint(position, state.initialWorldPosition, orientation, state.initialWorldRotation, c, state);
        if (position.equals(this.tmpOrigPos) && orientation.equals(this.tmpOrigOri))
            return;
        const parent = model.getParent();
        let localPos;
        let localOri;
        if (parent !== undefined) {
            parent.getWorldTransform(this.tmpParentMat);
            localPos = worldToLocalPosition(position, this.tmpParentMat);
            localOri = parent.getWorldOrientation().invert().multiply(orientation);
        }
        else {
            localPos = position;
            localOri = orientation;
        }
        model.setTransformComponents(localPos, localOri, scale, {
            metaData: this.constraintMeta,
        });
    }
    toWireFormat(c) {
        switch (c.type) {
            case ConstraintType.ROTATION: return { type: c.type, axis: c.axis, value1: c.snapAngle };
            case ConstraintType.MOVEMENT: return { type: c.type, axis: c.axis };
            case ConstraintType.RANGE: return { type: c.type, axis: c.axis, value1: c.min, value2: c.max };
            case ConstraintType.CONSTRAIN_TO_GRID: return { type: c.type, axis: c.axis, value1: c.gridSize };
            case ConstraintType.CONSTRAIN_TO_NAVMESH: return { type: c.type };
            default: throw new Error(`Unknown constraint type: ${c.type}`);
        }
    }
    /// Private constraint helper methods ///
    applyConstraint(pos, startPos, rot, startRot, c, state) {
        var _a;
        switch (c.type) {
            case ConstraintType.ROTATION:
                this.constrainRotation(rot, startRot, c.axis, c.value1);
                break;
            case ConstraintType.MOVEMENT:
                this.constrainMovement(pos, startPos, c.axis);
                break;
            case ConstraintType.RANGE:
                this.constrainRange(pos, c.axis, c.value1, c.value2);
                break;
            case ConstraintType.CONSTRAIN_TO_GRID:
                this.constrainToGrid(pos, c.axis, (_a = c.value1) !== null && _a !== void 0 ? _a : 1);
                break;
            case ConstraintType.CONSTRAIN_TO_NAVMESH:
                this.constrainToNavMesh(pos, state);
                break;
        }
    }
    constrainToNavMesh(pos, state) {
        var _a;
        const layerId = navMeshService.currentLayerId;
        // Initialize (or reinitialize after layer change) the node group
        if (state.navMeshNodeGroup === undefined || state.navMeshNodeGroup.layerId !== layerId) {
            this.tmpNavMeshEnd.set(pos.x, pos.y, pos.z);
            state.navMeshNodeGroup = navMeshService.createNodeGroup(layerId, this.tmpNavMeshEnd);
            state.navMeshPrevPos = this.tmpNavMeshEnd.clone();
            state.navMeshYOffset = undefined;
        }
        this.tmpNavMeshEnd.set(pos.x, pos.y, pos.z);
        const start = (_a = state.navMeshPrevPos) !== null && _a !== void 0 ? _a : this.tmpNavMeshEnd;
        const found = navMeshService.findPositionOnNavMesh(state.navMeshNodeGroup, start, this.tmpNavMeshEnd, this.tmpNavMeshOut);
        if (!found)
            return;
        // Record the Y offset on first successful navmesh contact
        if (state.navMeshYOffset === undefined)
            state.navMeshYOffset = pos.y - this.tmpNavMeshOut.y;
        if (state.navMeshPrevPos === undefined)
            state.navMeshPrevPos = this.tmpNavMeshOut.clone();
        else
            state.navMeshPrevPos.copy(this.tmpNavMeshOut);
        pos.x = this.tmpNavMeshOut.x;
        pos.z = this.tmpNavMeshOut.z;
        pos.y = this.tmpNavMeshOut.y + state.navMeshYOffset;
    }
    constrainRotation(rot, startRot, axis, snapAngle) {
        const rotAxis = this.tmpVec3a;
        switch (axis) {
            case ConstraintsAxis.X:
                rotAxis.set(1, 0, 0);
                break;
            case ConstraintsAxis.Y:
                rotAxis.set(0, 1, 0);
                break;
            case ConstraintsAxis.Z:
                rotAxis.set(0, 0, 1);
                break;
            case ConstraintsAxis.XY:
                this.constrainEulerRotation(rot, "ZYX", snapAngle);
                return;
            case ConstraintsAxis.XZ:
                this.constrainEulerRotation(rot, "YZX", snapAngle);
                return;
            case ConstraintsAxis.YX:
                this.constrainEulerRotation(rot, "ZXY", snapAngle);
                return;
            case ConstraintsAxis.YZ:
                this.constrainEulerRotation(rot, "YZX", snapAngle);
                return;
            case ConstraintsAxis.ZX:
                this.constrainEulerRotation(rot, "YXZ", snapAngle);
                return;
            case ConstraintsAxis.ZY:
                this.constrainEulerRotation(rot, "XYZ", snapAngle);
                return;
            default:
                rot.copy(startRot);
                return;
        }
        this.constrainAxisRotation(rot, rotAxis);
        if (snapAngle !== undefined && snapAngle !== 0) {
            const delta = this.tmpQuat.copy(startRot).invert().premultiply(rot);
            this.tmpVec4.setAxisAngleFromQuaternion(delta);
            const angle = snapAngle * Math.round(this.tmpVec4.w / snapAngle);
            delta.setFromAxisAngle(this.tmpVec3a.set(this.tmpVec4.x, this.tmpVec4.y, this.tmpVec4.z), angle);
            rot.multiplyQuaternions(startRot, delta);
        }
    }
    constrainEulerRotation(rot, order, snapAngle) {
        const euler = this.tmpEuler.setFromQuaternion(rot, order);
        if (snapAngle !== undefined && snapAngle !== 0) {
            euler.x = snapAngle * Math.round(euler.x / snapAngle);
            euler.y = snapAngle * Math.round(euler.y / snapAngle);
            euler.z = snapAngle * Math.round(euler.z / snapAngle);
        }
        switch (order) {
            case "XYZ":
            case "XZY":
                euler.x = 0;
                break;
            case "YXZ":
            case "YZX":
                euler.y = 0;
                break;
            case "ZXY":
            case "ZYX":
                euler.z = 0;
                break;
        }
        rot.setFromEuler(euler);
    }
    constrainAxisRotation(rot, axis) {
        const rotatedAxis = this.tmpVec3b.copy(axis).applyQuaternion(rot).normalize();
        const dot = Math.max(-1, Math.min(1, rotatedAxis.dot(axis)));
        const angle = Math.acos(dot);
        if (angle > 1e-6) {
            const crossAxis = rotatedAxis.cross(axis);
            const crossLen = crossAxis.length();
            if (crossLen < 1e-10)
                return;
            crossAxis.divideScalar(crossLen);
            rot.premultiply(this.tmpQuat.setFromAxisAngle(crossAxis, angle));
        }
    }
    constrainMovement(pos, startPos, axis) {
        switch (axis) {
            case ConstraintsAxis.X:
                pos.x = startPos.x;
                break;
            case ConstraintsAxis.Y:
                pos.y = startPos.y;
                break;
            case ConstraintsAxis.Z:
                pos.z = startPos.z;
                break;
            case ConstraintsAxis.XY:
            case ConstraintsAxis.YX:
                pos.x = startPos.x;
                pos.y = startPos.y;
                break;
            case ConstraintsAxis.XZ:
            case ConstraintsAxis.ZX:
                pos.x = startPos.x;
                pos.z = startPos.z;
                break;
            case ConstraintsAxis.YZ:
            case ConstraintsAxis.ZY:
                pos.y = startPos.y;
                pos.z = startPos.z;
                break;
            default: pos.copy(startPos);
        }
    }
    constrainRange(pos, axis, min, max) {
        switch (axis) {
            case ConstraintsAxis.X:
                if (min !== undefined)
                    pos.x = Math.max(pos.x, min);
                if (max !== undefined)
                    pos.x = Math.min(pos.x, max);
                break;
            case ConstraintsAxis.Y:
                if (min !== undefined)
                    pos.y = Math.max(pos.y, min);
                if (max !== undefined)
                    pos.y = Math.min(pos.y, max);
                break;
            case ConstraintsAxis.Z:
                if (min !== undefined)
                    pos.z = Math.max(pos.z, min);
                if (max !== undefined)
                    pos.z = Math.min(pos.z, max);
                break;
        }
    }
    constrainToGrid(pos, axis, gridSize) {
        switch (axis) {
            case ConstraintsAxis.X:
                pos.x = gridSize * Math.round(pos.x / gridSize);
                break;
            case ConstraintsAxis.Y:
                pos.y = gridSize * Math.round(pos.y / gridSize);
                break;
            case ConstraintsAxis.Z:
                pos.z = gridSize * Math.round(pos.z / gridSize);
                break;
            default:
                pos.x = gridSize * Math.round(pos.x / gridSize);
                pos.y = gridSize * Math.round(pos.y / gridSize);
                pos.z = gridSize * Math.round(pos.z / gridSize);
        }
    }
}
__decorate([
    FacetHandler(MovementConstraintFacet)
], MovementConstraintModule.prototype, "handleMovementConstraintFacet", null);
__decorate([
    FacetRemoveHandler(MovementConstraintFacet)
], MovementConstraintModule.prototype, "handleMovementConstraintFacetRemoved", null);
